import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../../App';
import { customerApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff, Zap, Phone, Lock } from 'lucide-react';

export default function AccountLoginPage() {
  const { loginCustomer } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Honor either `?next=` query param (from checkout flow) or location.state.from
  const nextParam = new URLSearchParams(location.search).get('next');
  const from = nextParam || location.state?.from?.pathname || '/account';

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!login || !password) { toast.error('Please fill in all fields'); return; }
    setLoading(true);
    try {
      const res = await customerApi.login({ login, password });
      loginCustomer(res.data.access_token, res.data.customer);
      toast.success(`Welcome back, ${res.data.customer.full_name}!`);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid phone/email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#0A0A0A' }}>
      {/* Hero top */}
      <div className="hero-gradient pb-6">
        <div className="max-w-[420px] mx-auto px-5 pt-10">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
              <Zap size={15} className="text-[#120F08]" />
            </div>
            <span className="font-['EB_Garamond'] text-[#F2F2F2] text-xl font-semibold">Scratch Unleashed</span>
          </div>
          <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] mb-1">Sign in</h1>
          <p className="text-[#B9B9B9] text-sm">Access your bookings and exclusive events</p>
        </div>
      </div>

      <div className="flex-1 max-w-[420px] mx-auto w-full px-5 py-6">
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-login-form">
          <div className="space-y-1.5">
            <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5"><Phone size={11} /> Phone or Email</Label>
            <Input
              data-testid="customer-login-input"
              placeholder="9876543210 or email@example.com"
              value={login}
              onChange={e => setLogin(e.target.value)}
              className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 placeholder:text-[#4A4A4A] h-12"
              autoComplete="username"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5"><Lock size={11} /> Password</Label>
            <div className="relative">
              <Input
                type={showPwd ? 'text' : 'password'}
                data-testid="customer-password-input"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 h-12 pr-10"
                autoComplete="current-password"
              />
              <button type="button" onClick={() => setShowPwd(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6F6F6F] hover:text-[#B9B9B9]">
                {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="customer-login-submit"
            disabled={loading}
            className="w-full btn-gold h-12 rounded-xl font-semibold text-base mt-2"
          >
            {loading ? <div className="w-4 h-4 border-2 border-[#120F08] border-t-transparent rounded-full animate-spin" /> : 'Sign In'}
          </Button>
        </form>

        <div className="mt-5 text-center space-y-3">
          <p className="text-[#B9B9B9] text-sm">
            New here?{' '}
            <Link to="/account/signup" className="text-[#D4A843] hover:underline font-medium">Create account</Link>
          </p>
          <p className="text-[#6F6F6F] text-xs">
            Just checking in?{' '}
            <Link to="/checkin" className="text-[#B9B9B9] hover:text-[#D4A843] underline">Guest check-in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
